import diff from 'deep-diff';
import shortid from 'shortid';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import cloneDeep from 'lodash/cloneDeep';
import maxBy from 'lodash/maxBy';
import sum from 'lodash/sum';
import isEqual from 'lodash/isEqual';
import difference from 'utils/difference';
import { put, fork, take, select, race } from 'redux-saga/effects';
import { takeFetch } from 'redux/helpers';
import * as serviceActions from '../../../../services/actions';
import * as actions from './actions';
import * as itemTypeActions from '../itemType/actions';
import currentStatus from './currentStatus/saga';


function* upsertStatus(localId) {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.UPSERT_STATUS);
  if (success) {
      if (success.data.statusCode === 1) {
          yield put(actions.save.end());
          yield put(actions.invalidateServer.begin([{ localId, serverId: success.data.id }]));
          yield put(itemTypeActions.invalidateServer.begin());
          ttb.Notifications.ShowSaveSuccessMessage('Project Configuration', 'All changes has been saved');
      }
      else {
          ttb.Notifications.ShowErrorMessage('Project Configuration', success.data.statusMessage);
      }
  } else {
    ttb.Notifications.ShowErrorMessage('Project Configuration', 'The changes have not been saved');
  }
}

function* getStatuses() {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.GET_STATUSES);
  if (success) {
    const { statuses, colors, statusBehaviours } = success.data;
    statuses.sort((a, b) => a.order - b.order);
    const statusOptions = statuses.map(value => ({
      value: cloneDeep(value),
      $$localId: shortid.generate(),
      changes: 0,
    }));
    yield put(actions.init.end({ statusOptions, colors, statusBehaviours, statuses }));
    const option = statusOptions[0];
    const localId = option && option.$$localId;
    yield put(actions.currentStatus.init.begin(localId));
  }
}

function* getActualStatuses(relations) {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.GET_STATUSES);
  if (success) {
    const { statuses } = success.data;
    statuses.sort((a, b) => a.order - b.order);
    const {
      statusOptions: previosStatusOptions,
      statuses: previosStatuses,
      currentStatus: selectedStatus,
       } = yield select(state => state.configuration.project.editor.status);

    const selectedIndex = findIndex(previosStatusOptions, x => x.$$localId === selectedStatus.$$localId);
    const statusOptions = statuses.map((x) => {
      const value = cloneDeep(x);
      const previosStatuseValue = find(previosStatuses, y => y.id === x.id);
      if (previosStatuseValue) {
        const previosStatusOption = find(previosStatusOptions, y => y.value.id === x.id);
        const changes = diff(previosStatuseValue, value);
        if (changes) {
          changes.forEach((change) => {
            diff.applyChange(previosStatusOption.value, value, change);
          });
        }
        return {
          ...previosStatusOption,
          value: {
            ...previosStatusOption.value,
            isRemoved: changes ? false : previosStatusOption.value.isRemoved,
          },
        };
      }
      return {
        value,
        $$localId: shortid.generate(),
        changes: 0,
      };
    });

    if (relations) {
      previosStatusOptions.forEach((x) => {
        if (!x.value.id && !find(relations, y => y.localId === x.$$localId)) {
          statusOptions.push(x);
        }
      });
    }

    yield put(actions.invalidateServer.end({ statuses, statusOptions }));
    const option = statusOptions[selectedIndex] || statusOptions[0];
    const localId = option && option.$$localId;
    yield put(actions.currentStatus.init.begin(localId));
    yield put(actions.invalidateServer.invalidate());
  }
}

function* init() {
  while (true) {
    const { projectId } = yield take(actions.INIT.BEGIN);
    yield put(serviceActions.api.configuration.project.getStatuses.request({
      projectId,
    }));
    yield fork(getStatuses);
  }
}

function* invalidateServer() {
  while (true) {
    const { relations } = yield take(actions.INVALIDATE_SERVER.BEGIN);
    const { projectId } = yield select(state => state.configuration.project.editor.status);
    if (projectId) {
      yield fork(getActualStatuses, relations);
      yield put(serviceActions.api.configuration.project.getStatuses.request({
        projectId,
      }));
    }
  }
}

function* save() {
  while (true) {
    yield take(actions.SAVE.BEGIN);
    const {
      projectId,
      currentStatus: selectedStatus,
       } = yield select(state => state.configuration.project.editor.status);
    yield fork(upsertStatus, selectedStatus.$$localId);
    yield put(serviceActions.api.configuration.project.upsertStatus.request({
      projectId,
      status: selectedStatus.value,
    }));
  }
}

function* add() {
  while (true) {
    yield take(actions.ADD.BEGIN);
    const { statusOptions: previosStatuses } = yield select(state => state.configuration.project.editor.status);
    const maxOrderStatus = maxBy(previosStatuses, x => x.value.order);
    const order = maxOrderStatus ? maxOrderStatus.value.order + 1 : 1;
    const localId = shortid.generate();
    const statusOptions = [
      ...previosStatuses,
      {
        $$localId: localId,
        added: true,
        value: {
          order,
          name: `New Status ${previosStatuses.length + 1}`,
          color: 0,
          items: [],
        },
      },
    ];
    yield put(actions.add.end({ statusOptions }));
    yield put(actions.currentStatus.init.begin(localId));
  }
}

function* remove() {
  while (true) {
    const { localId } = yield take(actions.REMOVE.BEGIN);
    const { statusOptions: previosStatuses } = yield select(state => state.configuration.project.editor.status);
    const statusOptions = previosStatuses.filter(x => x.$$localId !== localId);
    yield put(actions.remove.end({ statusOptions }));
    yield put(actions.currentStatus.init.begin(statusOptions[statusOptions.length - 1] && statusOptions[statusOptions.length - 1].$$localId));
  }
}

function* invalidate() {
  while (true) {
    yield race([
      take(actions.currentStatus.SET_NAME),
      take(actions.currentStatus.SET_COLOR),
      take(actions.currentStatus.SET_ORDER),
      take(actions.currentStatus.SET_ITEM_COLOR),
      take(actions.currentStatus.SET_COMPLETED_ON_SPRINT),
      take(actions.currentStatus.SET_CALCULATE_SPENT_TIME),
      take(actions.currentStatus.SET_REMOVED),
      take(actions.currentStatus.RESET_CHANGES.END),
      take(actions.ADD.END),
      take(actions.REMOVE.END),
      take(actions.INVALIDATE_SERVER.INVALIDATE),
    ]);
    const {
      statuses,
      statusOptions: previosStatuses,
      currentStatus: selectedStatus,
       } = yield select(state => state.configuration.project.editor.status);
    const newStatuses = previosStatuses.map((x) => {
      const res = x.$$localId === selectedStatus.$$localId && !isEqual(x.value, selectedStatus.value)
        ? { ...selectedStatus }
        : x;
      const statusValue = find(statuses, y => y.id === res.value.id);
      const changes = statusValue ? difference(res.value, statusValue) : 1;
      return res.changes === changes ? res : {
        ...res,
        changes,
      };
    });
    const statusOptions = isEqual(newStatuses, previosStatuses)
      ? previosStatuses
      : newStatuses;
    const allChanges = sum(statusOptions.map(x => x.changes));
    yield put(actions.invalidate({ statusOptions, changes: allChanges }));

    const newSelectedStatus = find(statusOptions, x => x.$$localId === selectedStatus.$$localId);
    if (newSelectedStatus && newSelectedStatus.changes !== selectedStatus.changes) {
      yield put(actions.currentStatus.invalidateChanges({ changes: newSelectedStatus.changes }));
    }
  }
}


export default function* status() {
  yield fork(currentStatus);
  yield fork(init);
  yield fork(save);
  yield fork(add);
  yield fork(remove);
  yield fork(invalidate);
  yield fork(invalidateServer);
}



// WEBPACK FOOTER //
// ../redux/store/configuration/project/editor/status/saga.js